import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FEATURE_FLAGS, isFeatureEnabled, getFeatureFlagStatus, isInDevMode } from '@/config/featureFlags';
import { Settings, RefreshCw, Wifi, WifiOff, Database } from 'lucide-react';
import { LanguageSelector } from './LanguageSelector';
import { useOfflineCache } from '@/hooks/useOfflineCache';

export const FeatureFlagController = () => {
  const [localFlags, setLocalFlags] = useState<Record<string, boolean>>(() => {
    const saved = localStorage.getItem('featureFlags');
    return saved ? JSON.parse(saved) : {};
  });
  const [isOpen, setIsOpen] = useState(false);
  const { getCacheStatus } = useOfflineCache();

  // Only show in development environments when dev mode is enabled
  const shouldShow = (import.meta.env.DEV || 
    window.location.hostname.includes('lovable') || 
    localStorage.getItem('forceDevControls') === 'true') && isInDevMode();
  
  if (!shouldShow) return null;

  const updateFeatureFlag = (key: string, enabled: boolean) => {
    const newFlags = { ...localFlags, [key]: enabled };
    setLocalFlags(newFlags);
    localStorage.setItem('featureFlags', JSON.stringify(newFlags));
    
    // Force page reload to apply changes
    window.location.reload();
  };

  const resetAllFlags = () => {
    localStorage.removeItem('featureFlags');
    setLocalFlags({});
    window.location.reload();
  };

  const getEffectiveState = (key: string) => {
    return localFlags.hasOwnProperty(key) ? localFlags[key] : FEATURE_FLAGS[key].enabled;
  };

  const status = getFeatureFlagStatus();
  const cacheStatus = getCacheStatus();

  if (!isOpen) {
    return (
      <div className="fixed bottom-4 left-4 z-50">
        <Button
          onClick={() => setIsOpen(true)}
          size="sm"
          variant="outline"
          className="bg-white shadow-lg border-orange-300 text-orange-800"
        >
          <Settings size={16} className="mr-2" />
          Feature Flags [DEV]
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 left-4 z-50 w-80">
      <Card className="shadow-lg border-orange-300">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">Feature Flags [DEV ONLY]</CardTitle>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={resetAllFlags}>
                <RefreshCw size={14} />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setIsOpen(false)}>
                ×
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-xs text-gray-600 mb-3 bg-orange-50 p-2 rounded">
            <strong>Development Mode:</strong> This controller is hidden in production builds<br/>
            Current Mode: {isInDevMode() ? 'DEV' : 'PUBLIC'}<br/>
            Environment: {status?.environment} | Version: {status?.currentVersion}
          </div>

          {/* Cache Status Display */}
          {cacheStatus.enabled && (
            <div className="p-2 bg-blue-50 border border-blue-200 rounded">
              <div className="flex items-center gap-2 mb-2">
                {cacheStatus.isOnline ? (
                  <Wifi className="h-3 w-3 text-green-600" />
                ) : (
                  <WifiOff className="h-3 w-3 text-orange-600" />
                )}
                <span className="text-xs font-medium text-blue-800">
                  {cacheStatus.isOnline ? 'Online' : 'Offline'}
                </span>
                {cacheStatus.hasCache && (
                  <Database className="h-3 w-3 text-blue-600" />
                )}
              </div>
              {cacheStatus.hasCache && (
                <div className="text-xs text-blue-600">
                  Cached: {cacheStatus.itemsCached?.posts || 0} posts, {cacheStatus.itemsCached?.casts || 0} casts
                </div>
              )}
            </div>
          )}

          {/* Language Translation Feature */}
          {getEffectiveState('LANGUAGE_TRANSLATION') && (
            <div className="p-2 bg-green-50 border border-green-200 rounded">
              <div className="text-xs font-medium mb-2 text-green-800">
                Language Translation (Active)
              </div>
              <LanguageSelector />
            </div>
          )}
          
          {Object.entries(FEATURE_FLAGS).map(([key, flag]) => {
            const effectiveState = getEffectiveState(key);
            const isOverridden = localFlags.hasOwnProperty(key);
            
            return (
              <div key={key} className="flex items-center justify-between space-x-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium truncate">{flag.name}</span>
                    {isOverridden && (
                      <Badge variant="secondary" className="text-xs">Override</Badge>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 truncate">{flag.description}</div>
                  <div className="text-xs text-gray-400">v{flag.version}</div>
                </div>
                <Switch
                  checked={effectiveState}
                  onCheckedChange={(checked) => updateFeatureFlag(key, checked)}
                />
              </div>
            );
          })}
          
          <div className="pt-2 border-t text-xs text-gray-500">
            Changes require page reload
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
